"use strict";
// Copyright © Veeam Software Group GmbH.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NewRequest = void 0;
const rxjs_1 = require("rxjs");
const ajax_1 = require("rxjs/ajax");
const operators_1 = require("rxjs/operators");
const Errors_1 = require("./Errors");
const helpers_1 = require("./helpers");
const types_1 = require("./types");
class NewRequest {
    constructor(config) {
        this.config = config;
        this.terminationController = new AbortController();
    }
    static trimAndRemoveStartSlash(url) {
        return url.trim().replace(/^\/+/, '');
    }
    static trimAndRemoveEndSlash(url) {
        return url.trim().replace(/\/+$/, '');
    }
    static getUrlWithQuery(url, queryParams) {
        if (!queryParams) {
            return url;
        }
        return [url, queryParams.toString()].join(url.includes('?') ? '&' : '?');
    }
    prepareUrl(urlParams) {
        var _a;
        const prefixUrl = (_a = (typeof this.config.prefixUrl === 'function'
            ? this.config.prefixUrl()
            : this.config.prefixUrl)) !== null && _a !== void 0 ? _a : '';
        const requestUrl = this.config.url;
        let url = '';
        if (typeof requestUrl === 'function') {
            url = requestUrl(prefixUrl, urlParams);
        }
        else {
            url = requestUrl;
            if (prefixUrl && !/[A-Za-z][A-Za-z0-9+\-.]*:\/\//.test(url)) {
                url = [
                    NewRequest.trimAndRemoveEndSlash(prefixUrl),
                    NewRequest.trimAndRemoveStartSlash(url)
                ].join('/');
            }
        }
        const paramsReplaceRegexp = /^((?:(?:[A-Za-z][A-Za-z0-9+\-.]*:\/\/)?[A-Za-z0-9+\-.]+(?::\d+)?)?[\/\?]+?.*?)?(?::(\w+))/g;
        while (paramsReplaceRegexp.test(url)) {
            url = url.replace(paramsReplaceRegexp, (match, startUrl = '', paramName) => {
                const value = urlParams === null || urlParams === void 0 ? void 0 : urlParams[paramName];
                if (!value && value !== 0) {
                    throw new Error(`Request error: param "${paramName}" must be defined`);
                }
                return `${startUrl}${value}`;
            });
        }
        return NewRequest.trimAndRemoveEndSlash(url);
    }
    createTransportConfig(loadConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            const { method = types_1.HTTP_METHOD.GET, mode = types_1.HTTP_CORS_MODE.CORS, credentials = types_1.HTTP_CREDENTIALS.OMIT, responseType = types_1.HTTP_RESPONSE_TYPE.JSON, headers = {}, headersProvider = (headers) => Promise.resolve(headers), urlParams } = this.config;
            return {
                method,
                url: NewRequest.getUrlWithQuery(this.prepareUrl(Object.assign(Object.assign({}, urlParams), loadConfig.urlParams)), loadConfig.queryParams),
                headers: yield headersProvider(Object.assign(Object.assign({}, headers), loadConfig.headers)),
                mode,
                credentials,
                body: loadConfig.body,
                responseType,
                signal: loadConfig.abortSignal
            };
        });
    }
    getDefaultTransport() {
        return (config) => {
            return (0, rxjs_1.of)(config).pipe((0, operators_1.map)((config) => {
                const { method, url, headers, body, mode, credentials, responseType } = config;
                return {
                    method,
                    async: true,
                    url,
                    headers,
                    body,
                    timeout: 0,
                    crossDomain: mode === types_1.HTTP_CORS_MODE.CORS,
                    withCredentials: credentials === types_1.HTTP_CREDENTIALS.INCLUDE,
                    responseType: responseType === types_1.HTTP_RESPONSE_TYPE.FORM_DATA ? '' : responseType
                };
            }), (0, operators_1.concatMap)((ajaxRequest) => (0, ajax_1.ajax)(ajaxRequest)), (0, operators_1.map)((ajaxResponse) => {
                return {
                    data: ajaxResponse.response,
                    meta: {
                        status: ajaxResponse.status,
                        responseHeaders: ajaxResponse.responseHeaders
                    }
                };
            }));
        };
    }
    throwIfSignalAbortedOrTerminated(abortSignal) {
        if (abortSignal === null || abortSignal === void 0 ? void 0 : abortSignal.aborted) {
            throw new Errors_1.NewRequestAbortError();
        }
        if (this.terminationController.signal.aborted) {
            throw new Errors_1.NewRequestTerminatedError();
        }
    }
    loadAsObservable(loadConfig = {}) {
        var _a, _b, _c;
        return (0, rxjs_1.of)(loadConfig).pipe((0, operators_1.concatMap)((loadConfig) => {
            this.throwIfSignalAbortedOrTerminated(loadConfig.abortSignal);
            return this.createTransportConfig(loadConfig);
        }), (0, operators_1.concatMap)((transportConfig) => {
            var _a;
            const transport = (_a = this.config.transport) !== null && _a !== void 0 ? _a : this.getDefaultTransport();
            return (0, rxjs_1.from)(transport(transportConfig));
        }), (0, helpers_1.createOperatorFromArray)([
            (0, operators_1.takeUntil)((0, rxjs_1.fromEvent)(this.terminationController.signal, 'abort')),
            (0, operators_1.throwIfEmpty)(() => new Errors_1.NewRequestTerminatedError()),
            loadConfig.abortSignal
                ? (0, operators_1.takeUntil)((0, rxjs_1.fromEvent)(loadConfig.abortSignal, 'abort'))
                : rxjs_1.identity,
            loadConfig.abortSignal ? (0, operators_1.throwIfEmpty)(() => new Errors_1.NewRequestAbortError()) : rxjs_1.identity
        ]), ((_a = this.config.retryNotifiers) === null || _a === void 0 ? void 0 : _a.length)
            ? (0, helpers_1.createOperatorFromArray)(this.config.retryNotifiers.map((retryNotifier) => (0, operators_1.retryWhen)(retryNotifier)))
            : rxjs_1.identity, (0, operators_1.catchError)((error) => {
            var _a, _b, _c, _d, _e;
            const mappedError = (_c = (_b = (_a = this.config).errorMapper) === null || _b === void 0 ? void 0 : _b.call(_a, error)) !== null && _c !== void 0 ? _c : error;
            (_e = (_d = this.config).errorHandler) === null || _e === void 0 ? void 0 : _e.call(_d, mappedError);
            return (0, rxjs_1.throwError)(() => mappedError);
        }), (0, operators_1.map)((response) => {
            var _a;
            const resultMapper = (_a = this.config.resultMapper) !== null && _a !== void 0 ? _a : ((result) => result);
            return resultMapper(response);
        }), (0, operators_1.take)(1), (0, operators_1.share)({
            resetOnComplete: (_b = this.config.resetOnCompleteOrError) !== null && _b !== void 0 ? _b : false,
            resetOnError: (_c = this.config.resetOnCompleteOrError) !== null && _c !== void 0 ? _c : false,
            resetOnRefCountZero: true
        }));
    }
    whenLoad(loadConfig = {}) {
        return this.loadAsObservable(loadConfig);
    }
    load$(loadConfig = {}) {
        return this.loadAsObservable(loadConfig);
    }
    load(loadConfig = {}) {
        return new Promise((resolve, reject) => {
            this.loadAsObservable(loadConfig).subscribe({
                next: resolve,
                error: reject
            });
        });
    }
    terminate() {
        this.terminationController.abort();
    }
}
exports.NewRequest = NewRequest;
//# sourceMappingURL=NewRequest.js.map